import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useBusy, useBusyKeys } from '../hooks/useBusy';
import { useConfirm } from '../hooks/useConfirm';
import { useAfterWrite } from '../hooks/useAfterWrite';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels } from '../i18n/labels';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUrlTab } from '../hooks/useUrlParam';
import AthleteCard from '../components/shared/AthleteCard';
import AthleteAvatar from '../components/shared/AthleteAvatar';
import Avatar from '../components/shared/Avatar';
import { athleteRecordActions } from '../components/shared/athleteRecordActions';
import EditAthleteModal from '../components/shared/EditAthleteModal';
import AthleteFieldsGroup, { EMPTY_RECORD_FORM, toCreateRecordDto, type AthleteFieldsForm } from '../components/shared/AthleteFieldsGroup';
import ManageCoachModal, { type ManageableCoach } from '../components/shared/ManageCoachModal';
import CoachRoleSelect from '../components/shared/CoachRoleSelect';
import ModalBackdrop from '../components/shared/ModalBackdrop';
import ModalHeader from '../components/shared/ModalHeader';
import ModalFooter from '../components/shared/ModalFooter';
import RoleBadge from '../components/shared/RoleBadge';
import CountryPicker from '../components/shared/CountryPicker';
import CountryDisplay from '../components/shared/CountryDisplay';
import AthleteJoinTeamModal from '../components/tournament/AthleteJoinTeamModal';
import ClaimRecordModal from '../components/shared/ClaimRecordModal';
import InviteAthleteModal from '../components/shared/InviteAthleteModal';
import ExistingAthleteNotice, { identityMatchOf, type IdentityMatch } from '../components/shared/ExistingAthleteNotice';
import ClaimCheckBadge from '../components/shared/ClaimCheckBadge';
import { PencilIcon, PeopleIcon } from '../components/shared/Icons';
import LogoEditOverlay from '../components/shared/LogoEditOverlay';
import ResolveActions from '../components/shared/ResolveActions';
import TabBar from '../components/shared/TabBar';
import { FacebookPill } from '../components/shared/FacebookPill';
import Skeleton from '../components/shared/Skeleton';
import BackLink from '../components/shared/BackLink';
import LoadFailure from '../components/shared/LoadFailure';
import ErrorState from '../components/shared/ErrorState';
import { logBackgroundError, record } from '../observability';
import { errorCode } from '../lib/httpError';
import { personName } from '../utils/personName';
import { weightText } from '../utils/weight';
import {
  getTeam, updateTeam, approveMember,
  getAthleteRecords, createAthleteRecord, deleteAthleteRecord, removeAthleteFromTeam,
  getTeamClaims, resolveClaimRequest, getTeamAthletes,
  uploadTeamLogo, deleteTeamLogo, claimTeam, assignCoachToTeam,
  getTeamAthleteRequests, resolveAthleteRequest, joinTeam, getUsers,
} from '../api/client';
import api from '../api/client';
import { BADGE_XS } from '../components/shared/badgeSize';
import Field from '../components/shared/Field';
import type {
  Team, AthleteEntry, AthleteRecord, AthleteRecordRow, AthleteRecordClaim, AthleteTeamRequest, UserListRow, MemberStatus, Gender, TeamMemberRole,
} from '../types';

type TeamTab = 'members' | 'requests' | 'join';

/** Team member as surfaced by GET /api/teams/:id — the backend JOINs
 *  users onto team_members and names the user_id as `id` in the response.
 *  Distinct from the generic TeamMember type (which models the raw
 *  team_members row). */
interface TeamMemberRow {
  id: string;            // user_id (not team_members.id)
  first_name: string;
  last_name: string;
  photo_url?: string;
  phone?: string;
  role: TeamMemberRole;
  status: MemberStatus;
}

/** The person coaches THIS team: a team manager (an admin is one), or a member
 *  of the team. `members` holds the approved rows, and a row is a head coach
 *  or a coach (the CHECK of `team_members.role`). */
function coachesTeam(members: readonly TeamMemberRow[] | undefined, userId: string | undefined, isTeamManager: boolean): boolean {
  return isTeamManager || !!members?.some(m => m.id === userId);
}

/** Team detail view: base Team + its members array. */
type TeamDetail = Team & { members?: TeamMemberRow[] };

/** Pending join request as returned by GET /api/teams/:id/members/pending. */
interface TeamPendingMemberRow {
  id: string;            // team_members.id
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  photo_url?: string;
  role: TeamMemberRole;
  joined_at: string;
}

/**
 * The page of one team, at `/teams/:id` (#595). The list of the teams is
 * `TeamList.tsx`. Until 2026-10-14 one component held the list and the team,
 * and a click on a team changed the state only: the URL stayed `/teams`.
 */
export default function TeamDetail() {
  const { user, hasRole } = useAuthStore();
  const { t } = useI18n();
  const { genderLabel, ageShort, boutCount, roleLabel } = useLabels();
  const [selectedTeam, setSelectedTeam] = useState<TeamDetail | null>(null);
  usePageTitle([selectedTeam?.name, t.nav.teams]);
  const [showJoinAthleteModal, setShowJoinAthleteModal] = useState(false);
  const [pendingMembers, setPendingMembers] = useState<TeamPendingMemberRow[]>([]);
  const [allAthletes, setAllAthletes] = useState<AthleteEntry[]>([]);
  const [athleteRecords, setAthleteRecords] = useState<AthleteRecordRow[]>([]);
  /** #663 — the id of the team whose lists all loaded; null while a load runs or when a list failed. */
  const [listsReadyFor, setListsReadyFor] = useState<string | null>(null);
  const [claims, setClaims] = useState<AthleteRecordClaim[]>([]);
  const [athleteRequests, setAthleteRequests] = useState<AthleteTeamRequest[]>([]);
  const [showAddRecord, setShowAddRecord] = useState(false);
  // #658 — the record of the same person that the create found.
  const [existing, setExisting] = useState<IdentityMatch | null>(null);
  // #658 — the invitation of an athlete of another team or of no team. `null`
  // keeps the modal closed; an object opens it, with a preselected record or none.
  const [invite, setInvite] = useState<{ initial: React.ComponentProps<typeof InviteAthleteModal>['initial'] } | null>(null);
  const [recordForm, setRecordForm] = useState<AthleteFieldsForm>(EMPTY_RECORD_FORM);
  // The notice names the record of the typed name and birth date: a new value drops it.
  useEffect(() => { setExisting(null); }, [recordForm.first_name, recordForm.last_name, recordForm.birth_date]);
  const { showError, showSuccess } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const [editingRecord, setEditingRecord] = useState<AthleteEntry | null>(null);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editTeamForm, setEditTeamForm] = useState({ name: '', location: '', country: 'LT', facebook_url: '', is_federation_member: false });
  const [logoBanner, setLogoBanner] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [managingCoach, setManagingCoach] = useState<ManageableCoach | null>(null);
  const [showAssignCoach, setShowAssignCoach] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignResults, setAssignResults] = useState<UserListRow[]>([]);
  const [assignRole, setAssignRole] = useState<TeamMemberRole>('head_coach');
  // #605 — the lock of Assign Coach: its ref stops also two taps in one task.
  const assigning = useBusy();
  // #552 — a tap on a result only selects the user; Assign Coach writes.
  // A stray tap while the list scrolled on a phone wrote a head coach.
  const [assignSelectedId, setAssignSelectedId] = useState('');
  // #605 — a write locks its element until the answer: a second tap sends no copy.
  const joining = useBusy();
  // Approve and Reject of one request row share one lock.
  const resolving = useBusyKeys();
  const addingRecord = useBusy();
  // Save and Delete of the Edit Team modal share one lock.
  const teamEdit = useBusy();
  // The archive of a record has a lock for each record: the modal closes at once.
  const archiving = useBusyKeys();
  // #664 — ONE lock for the upload and the removal of the logo (the two trash
  // buttons too): two logo writes at one time can end in either order.
  const logoWrite = useBusy();
  const claiming = useBusy();
  // After a sent claim the button goes away. The page mounts again for each team.
  const [claimSent, setClaimSent] = useState(false);

  const isAdmin = hasRole('admin');
  const isTeamManager = hasRole('admin', 'team_manager');
  const canManageTeamRoster = hasRole('admin', 'head_coach', 'coach', 'team_manager');

  // The person coaches THIS team (#461): a team manager (an admin is one), or
  // a member of the team. A member row is a head coach or a coach, so the row
  // holds no role test (#661, F17). The name says "this team": the check is
  // scoped to one row (#406, rule 2).
  const coachesThisTeam = coachesTeam(selectedTeam?.members, user?.id, isTeamManager);
  const isTeamHeadCoach = isAdmin || !!selectedTeam?.members?.some(m => m.id === user?.id && m.role === 'head_coach');
  // #457 — the team edit and the logo are for the head coach, a team manager
  // and an admin (teams.ts PATCH, upload.ts team-logo). Every coach saw the
  // buttons, and a coach got 403 ONLY_HC_EDIT_TEAM on save.
  const canEditTeam = !!selectedTeam && (isTeamHeadCoach || isTeamManager);
  // #663 — The server refuses DELETE /api/teams/:id for a head coach who is not an admin when the team
  // has an athlete record or another approved member (TEAM_NOT_EMPTY). The page does not offer the delete
  // then. An admin can delete a team with athletes, so an admin needs no list.
  // The page loads the active records only. An archived record is not in the lists, so the server can
  // still answer TEAM_NOT_EMPTY for a team with archived records only. The server also refuses a team with
  // registrations (TEAM_HAS_TOURNAMENT_REGISTRATIONS) or official periods (TEAM_HAS_OFFICIALS). The page
  // does not know these, and showError shows the answer.
  // The lists load after the team, and a failed list stays empty. A head coach sees the control only
  // when all the lists of THIS team loaded (`listsReadyFor`), else an empty list would read as an empty team.
  const teamNotEmpty = allAthletes.length > 0 || athleteRecords.length > 0
    || !!selectedTeam?.members?.some(m => m.id !== user?.id);
  const canDeleteTeam = isTeamHeadCoach && (isAdmin || (listsReadyFor === selectedTeam?.id && !teamNotEmpty));
  // #663 — on a touch screen the two 36 px buttons of the logo overlay need an 80 px thumbnail (36 + 8 + 36).
  const thumbSize = canEditTeam ? 'w-14 h-14 [@media(hover:none)]:w-20 [@media(hover:none)]:h-20' : 'w-14 h-14';
  // #595 — the membership of the person comes with the team (GET /api/teams/:id),
  // not from the list: the team page has no list.
  const isAlreadyMember = !!(selectedTeam?.my_role || selectedTeam?.my_status === 'pending');

  // The team of the URL. A notice links here too: five notice types link to
  // /teams/<id>, and until 2026-09-24 the link showed "Page not found".
  const { id: routeTeamId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState<unknown>(null);
  /** #627 — a list of the team (the athletes, the pending members, the records) did not load. */
  const [listsError, setListsError] = useState<unknown>(null);
  const openTeam = () => {
    setLoadError(null);
    if (routeTeamId) selectTeam({ id: routeTeamId } as Team).catch(err => {
      setLoadError(err);
      logBackgroundError('TeamDetail:load')(err);
    });
  };
  useEffect(() => {
    setSelectedTeam(null);
    openTeam();
  }, [routeTeamId]);

  // The latest load wins. StrictMode runs the open-from-link effect twice in
  // dev, and a quick click on a second team starts a second load: the late
  // answer of the older load set the tab back to Members after a click on
  // another tab, and could write the lists of the other team.
  const selectSeq = useRef(0);
  async function selectTeam(team: Team) {
    const seq = ++selectSeq.current;
    const stale = () => seq !== selectSeq.current;
    setListsReadyFor(null);
    const res = await getTeam(team.id);
    if (stale()) return;
    setSelectedTeam(res.data);
    setLogoBanner(false);

    // #627 — a list that fails after the team loaded shows an error with a
    // retry. Before, the page said "No members yet" or "No pending requests",
    // and a user read that as lost data. Each list loads on its own, so a
    // failed list does not stop the lists after it. The error stays until a
    // load of all the lists succeeds.
    let failed: unknown = null;
    const loadList = async <T,>(name: string, read: () => Promise<{ data: T }>, apply: (rows: T) => void) => {
      try {
        const { data } = await read();
        if (!stale()) apply(data);
      } catch (err) {
        logBackgroundError(`TeamDetail:${name}`)(err);
        failed ??= err;
      }
    };
    await loadList('athletes', () => getTeamAthletes(team.id), setAllAthletes);
    if (stale()) return;

    if (user) {
      // #461 — the claims (the claimants' emails) and the pending members
      // belong to the coaches of THIS team, admin and team_manager. The
      // role alone is not enough: a coach of another team got a 403 here.
      const detail: TeamDetail = res.data;
      const coachesLoadedTeam = coachesTeam(detail.members, user.id, isTeamManager);
      if (canManageTeamRoster) {
        if (coachesLoadedTeam) await loadList('pending-members', () => api.get(`/api/teams/${team.id}/members/pending`), setPendingMembers);
        else setPendingMembers([]);
        if (stale()) return;
        await loadList('records', () => getAthleteRecords(team.id), setAthleteRecords);
        if (stale()) return;
        if (coachesLoadedTeam) await loadList('claims', () => getTeamClaims(team.id), setClaims);
        else setClaims([]);
        if (stale()) return;
        // The queue of athlete requests (GET /api/teams/:id/athlete-requests).
        // The server lets in a coach of THIS team, an admin and a team manager,
        // the same people as `coachesLoadedTeam`. A failed read shows the list
        // error with Retry (#665, bug 50): the Requests tab shows only when a
        // count is above 0, so a swallowed 500 hid the tab. A coach of another
        // team gets 403 from the server, so the page does not ask.
        if (coachesLoadedTeam) await loadList('athlete-requests', () => getTeamAthleteRequests(team.id), setAthleteRequests);
        else setAthleteRequests([]);
      } else {
        // A viewer with no coach role, no team manager role and no admin role
        // may not read the queue.
        await loadList('records', () => getAthleteRecords(team.id), setAthleteRecords);
        if (stale()) return;
        setAthleteRequests([]);
      }
    } else {
      setAthleteRecords([]);
      setPendingMembers([]);
      setClaims([]);
      setAthleteRequests([]);
    }
    if (!stale()) {
      setListsError(failed);
      setListsReadyFor(failed ? null : team.id);
    }
  }

  /** The remove of the logo, for the banner and for the thumbnail (#661, F34).
   *  The question comes before the lock (#665). */
  async function removeLogo() {
    if (!selectedTeam || !(await confirm({ message: t.teams.removeLogoConfirm, confirmLabel: t.common.remove, destructive: true }))) return;
    const team = selectedTeam;
    await logoWrite.run(async () => {
      try {
        await deleteTeamLogo(team.id);
      } catch (err) { showError(err); return; }
      // #419 — the logo is gone. A failed reload is a warning, not an error.
      setLogoBanner(false);
      await afterWrite({ source: 'TeamDetail:remove-logo', success: t.teams.logoRemoved, refresh: () => selectTeam(team) });
    });
  }

  async function handleJoinTeam(role: 'athlete' | 'coach') {
    if (!selectedTeam) return;
    record({ event: 'team_join_clicked', props: { role, team_id: selectedTeam.id } });
    if (role === 'athlete') {
      // Post users-athlete-split refactor: athlete-join-team is a separate flow
      // that creates an athlete_record + athlete_team_request, gated by a modal.
      setShowJoinAthleteModal(true);
      return;
    }
    const team = selectedTeam;
    await joining.run(async () => {
      try {
        await joinTeam(team.id, role);
      } catch (err) { showError(err); return; }
      // `role` is 'coach' here: an athlete joins through the modal above
      // #605 — the reload hides the Join tab. The lock holds until then.
      await afterWrite({ source: 'TeamDetail:join', success: t.teams.joinRequestSentCoach, refresh: () => selectTeam(team) });
    });
  }

  async function handleApprove(userId: string, status: 'approved' | 'rejected') {
    if (!selectedTeam) return;
    const team = selectedTeam;
    const teamId = team.id;
    await resolving.run(`member:${userId}`, async () => {
      // A head-coach request of a team that has a head coach answers 409
      // TEAM_HAS_HEAD_COACH: the request stays in the list.
      try {
        await approveMember(teamId, userId, status);
      } catch (err) { showError(err); return; }
      setPendingMembers(m => m.filter(x => x.user_id !== userId));
      // #605 — the decision committed. An approved member changes the members of the team, so the team
      // loads again, as after an assign. A failed reload is a warning, not an error.
      await afterWrite({
        source: 'TeamDetail:approve',
        success: status === 'approved' ? t.teams.memberApproved : t.teams.memberRejected,
        refresh: status === 'approved' ? () => selectTeam(team) : undefined,
      });
    });
  }

  async function handleAddRecord(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTeam) return;
    const teamId = selectedTeam.id;
    await addingRecord.run(async () => {
      try {
        await createAthleteRecord(teamId, toCreateRecordDto(recordForm));
      } catch (err) {
        // #658 — the person has a record: the form shows it and the next step.
        const match = identityMatchOf(err);
        if (match) { setExisting(match); return; }
        showError(err); return;
      }
      // #419 — the record exists now. A failed reload is a warning, not an error: the modal
      // closes, so a second Save cannot send the record again.
      setShowAddRecord(false);
      setRecordForm(EMPTY_RECORD_FORM);
      await afterWrite({
        source: 'TeamDetail:add-record',
        success: t.teams.recordAdded,
        refresh: async () => {
          const [ar, unified] = await Promise.all([
            getAthleteRecords(teamId),
            getTeamAthletes(teamId),
          ]);
          setAthleteRecords(ar.data);
          setAllAthletes(unified.data);
        },
      });
    });
  }

  async function handleArchiveRecord(id: string, name: string) {
    if (!(await confirm({ message: t.teams.archiveConfirm.replace('{name}', name), confirmLabel: t.teams.archive, destructive: true }))) return;
    // #605 — the edit modal closes only after the answer Archive. A Cancel keeps the unsaved edits.
    setEditingRecord(null);
    await archiving.run(id, async () => {
      try {
        await deleteAthleteRecord(id);
        setAthleteRecords(a => a.filter(x => x.id !== id));
        setAllAthletes(a => a.filter(x => x.id !== id));
        showSuccess(t.teams.archived.replace('{name}', name));
      } catch (err) { showError(err); }
    });
  }

  async function handleRemoveFromTeam(id: string, name: string) {
    if (!selectedTeam) return;
    if (!(await confirm({ message: t.teams.removeAthleteConfirm.replace('{name}', name).replace('{team}', selectedTeam.name), confirmLabel: t.teams.removeFromTeam, destructive: true }))) return;
    setEditingRecord(null);
    await archiving.run(id, async () => {
      try {
        await removeAthleteFromTeam(id);
        setAthleteRecords(a => a.filter(x => x.id !== id));
        setAllAthletes(a => a.filter(x => x.id !== id));
        showSuccess(t.teams.removedFromTeam.replace('{name}', name));
      } catch (err) { showError(err); }
    });
  }

  async function handleClaim(claimId: string, status: 'approved' | 'rejected') {
    await resolving.run(`claim:${claimId}`, async () => {
      const recordId = claims.find(x => x.id === claimId)?.athlete_record_id;
      try {
        await resolveClaimRequest(claimId, status);
      } catch (err) {
        // #569 — the approval of a rival claim resolved this claim meanwhile.
        const code = errorCode(err);
        if (code === 'ALREADY_RESOLVED') setClaims(c => c.filter(x => x.id !== claimId));
        // #605 — a toast for each refusal. A rethrow showed nothing for a network error.
        showError(err);
        return;
      }
      // #569 — an approval also rejects the rival claims of the record.
      setClaims(c => c.filter(x => x.id !== claimId && !(status === 'approved' && x.athlete_record_id === recordId)));
      showSuccess(status === 'approved' ? t.teams.claimApproved : t.teams.claimRejected);
    });
  }

  async function handleAthleteRequest(requestId: string, status: 'approved' | 'rejected') {
    if (!selectedTeam) return;
    const teamId = selectedTeam.id;
    await resolving.run(`athleteRequest:${requestId}`, async () => {
      try {
        await resolveAthleteRequest(teamId, requestId, status);
      } catch (err) { showError(err); return; }
      setAthleteRequests(r => r.filter(x => x.id !== requestId));
      // #419 — the decision committed. A failed reload is a warning, not an error.
      await afterWrite({
        source: 'TeamDetail:athlete-request',
        success: status === 'approved' ? t.teams.athleteRequestApproved : t.teams.athleteRequestRejected,
        // Refresh athletes list — approval moved AR.team_id into this team.
        refresh: status === 'approved' ? async () => { setAllAthletes((await getTeamAthletes(teamId)).data); } : undefined,
      });
    });
  }

  // #569 — "This is me" opens the claim dialog: it asks for the birth date of
  // the record and sends the claim (#426: one request in flight at a time).
  const [claimTarget, setClaimTarget] = useState<AthleteRecordRow | null>(null);
  async function handleClaimed() {
    setClaimTarget(null);
    const teamId = selectedTeam?.id;
    // #419 — the claim is sent: a failed read is a warning, not an error
    await afterWrite({
      source: 'TeamDetail:claimed',
      success: t.teams.selfClaimSubmitted,
      refresh: teamId ? async () => {
        const ar = await getAthleteRecords(teamId);
        setAthleteRecords(ar.data);
        const unified = await getTeamAthletes(teamId);
        setAllAthletes(unified.data);
      } : undefined,
    });
  }

  // #664 — the search runs on each key. The number of the newest text: the answer of an older text
  // must not fill the list of a newer one, or of a box that the user cleared.
  const searchSeq = useRef(0);
  async function searchUsersForAssign(q: string) {
    setAssignSearch(q);
    const seq = ++searchSeq.current;
    const stale = () => seq !== searchSeq.current;
    if (q.trim().length < 2) { setAssignResults([]); return; }
    try {
      const res = await getUsers({ q: q.trim() });
      if (stale()) return;
      // Filter out users already coaches in this team
      const existingCoachIds = new Set<string>(selectedTeam?.members?.map(m => m.id));
      setAssignResults(res.data.filter(u => !existingCoachIds.has(u.id)));
    } catch (err) { if (stale()) return; setAssignResults([]); showError(err); }  // #559: the empty list said "no person found"
  }

  async function handleAssignCoach(userId: string, userName: string) {
    if (!selectedTeam) return;
    const team = selectedTeam;
    await assigning.run(async () => {
      try {
        await assignCoachToTeam(team.id, userId, assignRole);
      } catch (err) { showError(err); return; }
      // #419 — the coach is in the team now. A failed reload is a warning, not an error.
      setShowAssignCoach(false);
      setAssignSearch('');
      searchSeq.current++;   // #664 — a search in flight does not refill the list that was just cleared
      setAssignResults([]);
      await afterWrite({
        source: 'TeamDetail:assign-coach',
        success: (assignRole === 'head_coach' ? t.teams.assignedHeadCoach : t.teams.assignedCoach).replace('{name}', userName),
        refresh: () => selectTeam(team),
      });
    });
  }

  const pendingCount = pendingMembers.length;
  const claimsCount = claims.length;
  const athleteRequestsCount = athleteRequests.length;

  const coachTabs: { id: TeamTab; label: string }[] = [
    { id: 'members', label: `${t.teams.members} (${allAthletes.length + (selectedTeam?.members?.length ?? 0)})` },
    ...(coachesThisTeam && (pendingCount + claimsCount + athleteRequestsCount > 0) ? [
      { id: 'requests' as TeamTab, label: `${t.teams.requests}${pendingCount + claimsCount + athleteRequestsCount ? ` (${pendingCount + claimsCount + athleteRequestsCount})` : ''}` },
    ] : []),
    ...(!isAlreadyMember && user ? [{ id: 'join' as TeamTab, label: t.teams.joinTeam }] : []),
  ];

  // #595 — the tab is in the URL (`?tab=`). A tab that the data does not show
  // (yet) shows the members, and the read never writes the URL. The call stays
  // above the early return, so the hook order does not change.
  const [tab, setTab] = useUrlTab(coachTabs.map(x => x.id), 'members');

  if (!selectedTeam) {
    if (loadError) {
      return (
        <div className="space-y-4">
          <BackLink to="/teams" label={t.common.allTeams} />
          <LoadFailure error={loadError} onRetry={openTeam}
            notFound={{ icon: <PeopleIcon className="w-12 h-12" />, title: t.common.teamNotFound, to: '/teams', linkLabel: t.common.allTeams }} />
        </div>
      );
    }
    return <div className="space-y-4"><Skeleton.List shape="section" count={2} /></div>;
  }

  return (
    <div className="space-y-4">
      <BackLink to="/teams" label={t.common.allTeams} />

      {showJoinAthleteModal && selectedTeam && (
        <AthleteJoinTeamModal
          teamId={selectedTeam.id}
          teamName={selectedTeam.name}
          onDone={() => {
            setShowJoinAthleteModal(false);
            void afterWrite({ source: 'TeamDetail:athlete-join', refresh: () => selectTeam(selectedTeam) });
          }}
          onClose={() => setShowJoinAthleteModal(false)}
        />
      )}

      {invite && selectedTeam && (
        <InviteAthleteModal teamId={selectedTeam.id} teamName={selectedTeam.name}
          initial={invite.initial} onClose={() => setInvite(null)} />
      )}
      {claimTarget && (
        <ClaimRecordModal record={claimTarget} onClaimed={handleClaimed} onClose={() => setClaimTarget(null)} />
      )}

      {/* Edit Team Modal */}
      {showEditTeam && selectedTeam && (
        <ModalBackdrop onClose={() => setShowEditTeam(false)} maxWidth="sm" ariaLabel={t.teams.editTeam}>
            <ModalHeader title={t.teams.editTeam} />
            <form onSubmit={async (e) => {
              e.preventDefault();
              await teamEdit.run(async () => {
                try {
                  await updateTeam(selectedTeam.id, editTeamForm);
                } catch (err) { showError(err); return; }
                // #419 — the change committed. A failed reload is a warning, not an error.
                setShowEditTeam(false);
                await afterWrite({ source: 'TeamDetail:edit-team', success: t.teams.teamUpdated, refresh: () => selectTeam(selectedTeam) });
              });
            }} className="space-y-3">
              <Field label={t.teams.teamName} required>
                <input className="input" value={editTeamForm.name}
                  onChange={e => setEditTeamForm(f => ({ ...f, name: e.target.value }))} />
              </Field>
              <Field label={t.common.location}>
                <input className="input" value={editTeamForm.location}
                  onChange={e => setEditTeamForm(f => ({ ...f, location: e.target.value }))} />
              </Field>
              <Field label={t.country.label}>
                <CountryPicker value={editTeamForm.country} onChange={code => setEditTeamForm(f => ({ ...f, country: code }))} />
              </Field>
              <Field label={t.teams.facebookPage}>
                <input type="url" className="input" placeholder="https://facebook.com/yourteam" value={editTeamForm.facebook_url}
                  onChange={e => setEditTeamForm(f => ({ ...f, facebook_url: e.target.value }))} />
              </Field>
              {isTeamManager && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editTeamForm.is_federation_member}
                    onChange={e => setEditTeamForm(f => ({ ...f, is_federation_member: e.target.checked }))}
                    className="w-4 h-4 rounded-sm border-gray-300" />
                  <span className="text-sm">{t.teams.federationMember}</span>
                </label>
              )}
              <ModalFooter saveAsSubmit busy={teamEdit.busy}
                actions={canDeleteTeam ? [{
                  label: t.common.delete, destructive: true, disabled: teamEdit.busy, testId: 'team-delete',
                  onClick: async () => {
                    if (teamEdit.busy) return;
                    if (!(await confirm({ message: t.teams.deleteTeamConfirm.replace('{name}', selectedTeam.name), confirmLabel: t.common.delete, destructive: true }))) return;
                    await teamEdit.run(async () => {
                      try {
                        await api.delete(`/api/teams/${selectedTeam.id}`);
                        setShowEditTeam(false);
                        showSuccess(t.teams.teamDeleted);
                        // The team is gone: the list, with no entry for Back to the team.
                        navigate('/teams', { replace: true });
                      } catch (err) { showError(err); }
                    });
                  },
                }] : undefined}
                onCancel={() => setShowEditTeam(false)} />
            </form>
        </ModalBackdrop>
      )}

      {/* Assign Coach Modal */}
      {showAssignCoach && selectedTeam && (
        <ModalBackdrop onClose={() => setShowAssignCoach(false)} maxWidth="sm" ariaLabel={t.teams.assignCoach}>
            <ModalHeader title={t.teams.assignCoach} />
            <div className="space-y-3">
              <Field label={t.teams.assignRole}>
                <CoachRoleSelect value={assignRole} onChange={setAssignRole} />
              </Field>
              <Field label={t.teams.searchUser}>
                <input className="input" placeholder={t.admin.searchUsers} value={assignSearch}
                  onChange={e => searchUsersForAssign(e.target.value)} data-autofocus />
              </Field>
              {assignResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {assignResults.map(u => (
                    <button type="button" key={u.id} disabled={assigning.busy} data-testid="assign-coach-result"
                      onClick={() => setAssignSelectedId(u.id)} aria-pressed={assignSelectedId === u.id}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors disabled:opacity-50 ${
                        assignSelectedId === u.id ? 'bg-gray-100 ring-1 ring-inset ring-gray-900' : 'hover:bg-gray-50'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{personName(u.first_name, u.last_name)}</div>
                        <div className="text-xs text-gray-500 truncate">{u.email}</div>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {u.roles?.map((r: string) => (
                          <RoleBadge key={r} role={r} />
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {assignSearch.length >= 2 && assignResults.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-2">{t.common.noData}</p>
              )}
            </div>
            {(() => {
              const sel = assignResults.find(u => u.id === assignSelectedId);
              return (
                <ModalFooter
                  onSave={() => { if (sel) handleAssignCoach(sel.id, personName(sel.first_name, sel.last_name)); }}
                  busy={assigning.busy}
                  saveLabel={t.teams.assignCoach}
                  saveDisabled={!sel}
                  onCancel={() => setShowAssignCoach(false)}
                />
              );
            })()}
        </ModalBackdrop>
      )}

      {managingCoach && selectedTeam && (
        <ManageCoachModal
          teamId={selectedTeam.id}
          teamName={selectedTeam.name}
          coach={managingCoach}
          onUpdated={async () => {
            const res = await getTeam(selectedTeam.id);
            setSelectedTeam(res.data);
            setManagingCoach(null);
          }}
          onClose={() => setManagingCoach(null)}

        />
      )}

      {/* ── Team detail ──────────────────────────────────────────────────── */}
      {selectedTeam && (
        <div className="space-y-4">
            <div className="card overflow-hidden">

              {/* Hidden file input — shared by logo + banner click */}
              <input
                ref={logoInputRef}
                type="file"
                data-testid="team-logo-file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    await logoWrite.run(async () => {
                      try {
                        await uploadTeamLogo(selectedTeam.id, file);
                      } catch (err) { showError(err); return; }
                      // #419 — the logo is stored. A failed reload is a warning, not an error.
                      setLogoBanner(false);
                      await afterWrite({ source: 'TeamDetail:logo', success: t.teams.logoUpdated, refresh: () => selectTeam(selectedTeam) });
                    });
                  } finally {
                    e.target.value = '';
                  }
                }}
              />

              {/* Banner mode — wide image shown full-width above the info row */}
              {selectedTeam.logo_url && logoBanner && (
                <div className="relative group">
                  <img src={selectedTeam.logo_url} alt="banner" className="w-full max-h-48 object-cover" />
                  {canEditTeam && (
                    <LogoEditOverlay size="lg" onChange={() => logoInputRef.current?.click()} changeDisabled={logoWrite.busy}
                      onRemove={removeLogo} removeDisabled={logoWrite.busy}
                      changeLabel={t.teams.changeLogo} removeLabel={t.teams.removeLogo} />
                  )}
                </div>
              )}

            {/* The header row wraps on a phone: the claim button then takes its
                own full-width line under the team, and the name keeps the width. */}
            <div className="p-4 border-b border-gray-100 flex flex-wrap sm:flex-nowrap items-start gap-4">

              {/* Logo mode — square thumbnail */}
              {(!selectedTeam.logo_url || !logoBanner) && (
                <div className="relative shrink-0 group">
                  {selectedTeam.logo_url
                    ? <img
                        src={selectedTeam.logo_url}
                        alt={selectedTeam.name}
                        className={`${thumbSize} rounded-xl object-cover`}
                        onLoad={e => {
                          const img = e.currentTarget;
                          setLogoBanner(img.naturalWidth / img.naturalHeight > 1.5);
                        }}
                      />
                    : <div className={`${thumbSize} rounded-xl bg-gray-100 flex items-center justify-center text-gray-400`}><PeopleIcon className="w-8 h-8" /></div>
                  }
                  {canEditTeam && (
                    <LogoEditOverlay size="sm" className="rounded-xl" onChange={() => logoInputRef.current?.click()} changeDisabled={logoWrite.busy}
                      onRemove={selectedTeam.logo_url ? removeLogo : undefined} removeDisabled={logoWrite.busy}
                      changeLabel={t.teams.changeLogo} removeLabel={t.teams.removeLogo} />
                  )}
                </div>
              )}

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h1 data-testid="team-detail-name" className="font-bold text-lg break-words min-w-0">{selectedTeam.name}</h1>
                    {selectedTeam.country && <CountryDisplay code={selectedTeam.country} size="md" />}
                    {selectedTeam.is_federation_member && <span className="badge text-xs bg-emerald-50 text-emerald-700 border-emerald-200">LMF</span>}
                    {canEditTeam && (
                      <div className="flex items-center gap-1.5">
                        {/* Edit pencil */}
                        <button type="button" title={t.teams.editDetails}
                          onClick={() => {
                            setEditTeamForm({ name: selectedTeam.name || '', location: selectedTeam.location || '', country: selectedTeam.country || 'LT', facebook_url: selectedTeam.facebook_url || '', is_federation_member: !!selectedTeam.is_federation_member });
                            setShowEditTeam(true);
                          }}
                          aria-label={t.teams.editDetails} className="btn-icon">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {/* Logo managed via hover overlay on image */}
                      </div>
                    )}
                  </div>
                  {selectedTeam.location && <p className="text-sm text-gray-500">{selectedTeam.location}</p>}
                  {/* Unmanaged badge */}
                  {!selectedTeam.members?.length && (
                    <span className="badge badge-amber text-xs mt-1">{t.teams.unmanaged}</span>
                  )}
                  {selectedTeam.facebook_url && <FacebookPill url={selectedTeam.facebook_url} testId="team-facebook" className="mt-1" />}
                </div>
                {/* Claim as Head Coach: at the right on a wide screen, on its own
                    full-width line on a phone (the header row wraps). */}
                {(() => {
                  const hasCoach = !!selectedTeam.members?.length;
                  if (!hasCoach && user && hasRole('coach', 'head_coach') && !isAlreadyMember && !claimSent) return (
                    <button data-testid="team-claim-head-coach" disabled={claiming.busy} onClick={() => claiming.run(async () => {
                      try {
                        await claimTeam(selectedTeam.id, 'head_coach');
                      } catch (err) { showError(err); return; }
                      // #605 — the claim is sent: the button goes, so no second claim.
                      setClaimSent(true);
                      showSuccess(t.teams.teamClaimSubmitted);
                    })} className="btn-primary btn-sm w-full sm:w-auto shrink-0 max-sm:min-h-10">
                      {t.teams.claimAsHeadCoach}
                    </button>
                  );
                  return null;
                })()}
              </div>

              <TabBar size="compact" tabs={coachTabs} activeTab={tab} onChange={id => setTab(id as TeamTab)} />

              <div className="p-4">

                {/* #627 — a list of the team did not load: the error and a retry
                    on each tab, never an empty list. */}
                {!!listsError && <div className="mb-3"><ErrorState compact onRetry={openTeam} /></div>}

                {/* Members */}
                {tab === 'members' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {coachesThisTeam && (
                        <button onClick={() => { setShowAddRecord(true); setExisting(null); setRecordForm(EMPTY_RECORD_FORM); }}
                          className="btn-primary btn-sm">+ {t.teams.addAthleteRecord}</button>
                      )}
                      {coachesThisTeam && (
                        <button onClick={() => setInvite({ initial: null })} data-testid="team-invite-athlete"
                          className="btn-outline btn-sm">+ {t.teams.invite.button}</button>
                      )}
                      {isTeamManager && selectedTeam && (
                        <button onClick={() => { setShowAssignCoach(true); setAssignSearch(''); searchSeq.current++; setAssignResults([]); setAssignSelectedId(''); setAssignRole('head_coach'); }}
                          className="btn-outline btn-sm">+ {t.teams.assignCoach}</button>
                      )}
                    </div>
                    {showAddRecord && (
                      <ModalBackdrop onClose={() => setShowAddRecord(false)} maxWidth="md" ariaLabel={t.teams.addAthleteRecord}>
                          <ModalHeader title={t.teams.addAthleteRecord} />
                          <form onSubmit={handleAddRecord} className="space-y-3">
                            <AthleteFieldsGroup form={recordForm} onChange={setRecordForm} required
                              testIds={{ first: 'record-first_name', last: 'record-last_name' }} />
                            {existing && selectedTeam && (
                              <ExistingAthleteNotice match={existing} teamId={selectedTeam.id} canInvite={coachesThisTeam}
                                onInvited={() => { setExisting(null); setShowAddRecord(false); }} />
                            )}
                            <ModalFooter saveAsSubmit busy={addingRecord.busy}
                              onCancel={() => setShowAddRecord(false)} />
                          </form>
                      </ModalBackdrop>
                    )}

                    {(selectedTeam.members?.length ?? 0) > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t.teams.coaches}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {selectedTeam.members?.map(m => (
                            <div key={m.id} className="card p-3 flex items-start gap-3">
                              <Avatar firstName={m.first_name} lastName={m.last_name} photoUrl={m.photo_url} size="md" />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{personName(m.first_name, m.last_name)}</div>
                                {m.phone && <div className="text-xs text-gray-400 truncate">{m.phone}</div>}
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  <RoleBadge role={m.role} size="xs" />
                                  {m.id === user?.id && <span className={`badge ${BADGE_XS} bg-green-50 text-green-700`}>{t.common.you}</span>}
                                </div>
                              </div>
                              {canEditTeam && m.id !== user?.id && (
                                <button onClick={() => setManagingCoach({ id: m.id, first_name: m.first_name, last_name: m.last_name, role: m.role })}
                                  className="btn-icon shrink-0" title={t.common.edit} aria-label={t.common.edit}>
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {allAthletes.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t.teams.athletes} ({allAthletes.length})</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {allAthletes.map(a => (
                            <AthleteCard
                              key={`${a.source}-${a.id}`}
                              athlete={a}
                              showTeam={false}
                              compact
                              to={`/athletes/${a.id}`}
                              extraBadges={<>
                                {user && a.source === 'record' && a.claim_status !== 'claimed' && <span className={`badge badge-amber ${BADGE_XS}`}>{t.teams.unclaimed}</span>}
                                {a.claim_status === 'pending' && <span className={`badge ${BADGE_XS} bg-orange-50 text-orange-700`}>{t.teams.claimsPending}</span>}
                                {a.claim_status === 'claimed' && <span className={`badge badge-green ${BADGE_XS}`}>{t.teams.claimed}</span>}
                              </>}
                              actions={<>
                                {coachesThisTeam && a.id !== user?.id && (
                                  <button onClick={() => setEditingRecord(a)} className="btn-icon" title={t.common.edit} aria-label={t.common.edit}>
                                    <PencilIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </>}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {!listsError && allAthletes.length === 0 && !selectedTeam.members?.length && (
                      <p className="text-sm text-gray-400 text-center py-4">{t.teams.noMembers}</p>
                    )}
                  </div>
                )}

                {/* Edit athlete modal — opens for both claimed and unclaimed records */}
                {editingRecord && (
                  <EditAthleteModal
                    athlete={editingRecord}
                    onSaved={updated => {
                      const patch: Partial<AthleteRecord> = {
                        first_name: updated.first_name,
                        last_name: updated.last_name,
                        gender: (updated.gender || undefined) as Gender | undefined,
                        ...(updated.birth_date ? { birth_date: updated.birth_date } : {}),
                        experience_bouts: updated.experience_bouts,
                        current_weight_kg: updated.current_weight_kg ?? undefined,
                      };
                      setAthleteRecords(prev => prev.map(r => r.id === editingRecord.id ? { ...r, ...patch } : r));
                      setAllAthletes(prev => prev.map(a => a.id === editingRecord.id ? { ...a, ...patch } : a));
                      setEditingRecord(null);
                    }}
                    onClose={() => setEditingRecord(null)}

                    // #658 — only an admin archives a record. A coach of the team
                    // removes the athlete from the team; the record stays public.
                    recordActions={athleteRecordActions(t, coachesThisTeam ? {
                      onRemoveFromTeam: () => handleRemoveFromTeam(editingRecord.id, personName(editingRecord.first_name, editingRecord.last_name)),
                      onArchive: isAdmin ? () => handleArchiveRecord(editingRecord.id, personName(editingRecord.first_name, editingRecord.last_name)) : undefined,
                      disabled: archiving.isBusy(editingRecord.id),
                    } : {})}
                  />
                )}

                {/* Requests (combined Claims + Pending) */}
                {tab === 'requests' && coachesThisTeam && (
                  <div className="space-y-3">
                    {pendingMembers.map(m => (
                      <div key={m.id} className="card p-3 flex flex-wrap sm:flex-nowrap items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{personName(m.first_name, m.last_name)}</div>
                          <div className="text-xs text-gray-500 break-words">{m.email} · {t.teams.wantsToJoinAs} <strong>{roleLabel(m.role)}</strong></div>
                        </div>
                        <ResolveActions busy={resolving.isBusy(`member:${m.user_id}`)} onResolve={status => handleApprove(m.user_id, status)} />
                      </div>
                    ))}
                    {/* #569 — the server puts the rival claims of one record together. */}
                    {claims.map(claim => (
                      <div key={claim.id} data-claim-id={claim.id} className="card p-3 flex flex-wrap sm:flex-nowrap items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{personName(claim.first_name, claim.last_name)}</div>
                          <div className="text-xs text-gray-500 break-words">
                            {t.teams.claimedBy}: <span className="font-medium">{personName(claim.req_first, claim.req_last)}</span> ({claim.req_email})
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            <ClaimCheckBadge verified={claim.birth_date_verified} />
                            {claims.some(c => c.id !== claim.id && c.athlete_record_id === claim.athlete_record_id) && (
                              <span data-testid="rival-claim" className={`badge badge-amber ${BADGE_XS}`}>{t.teams.rivalClaim}</span>
                            )}
                          </div>
                        </div>
                        <ResolveActions busy={resolving.isBusy(`claim:${claim.id}`)} onResolve={status => handleClaim(claim.id, status)} />
                      </div>
                    ))}
                    {athleteRequests.map(req => (
                      <div key={req.id} className="card p-3 flex flex-wrap sm:flex-nowrap items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{personName(req.first_name, req.last_name)}</div>
                          <div className="text-xs text-gray-500">
                            {t.teams.wantsToJoinAsAthlete}{req.old_team_name ? ` ${t.teams.currentlyIn.replace('{team}', req.old_team_name)}` : ''}
                            {req.gender ? ` · ${genderLabel(req.gender)}` : ''}
                            {req.age != null ? ` · ${ageShort(req.age)}` : ''}
                          </div>
                        </div>
                        <ResolveActions busy={resolving.isBusy(`athleteRequest:${req.id}`)} onResolve={status => handleAthleteRequest(req.id, status)} />
                      </div>
                    ))}
                    {!listsError && pendingMembers.length === 0 && claims.length === 0 && athleteRequests.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-4">{t.teams.noPendingRequests}</p>
                    )}
                  </div>
                )}

                {/* Join */}
                {tab === 'join' && user && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                      {t.teams.requestToJoin} <strong>{selectedTeam.name}</strong>. {t.teams.headCoachReview}
                    </p>
                    <div>
                      <h3 className="font-semibold text-sm mb-3">{t.teams.joinAsNewMember}</h3>
                      <div className="flex gap-2">
                        <button onClick={() => handleJoinTeam('athlete')} disabled={joining.busy} className="btn-outline btn-sm flex-1">{t.teams.joinAsAthlete}</button>
                        <button onClick={() => handleJoinTeam('coach')} disabled={joining.busy} className="btn-outline btn-sm flex-1">{t.teams.joinAsCoach}</button>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 pt-4">
                      <h3 className="font-semibold text-sm mb-2">{t.teams.claimRecord}</h3>
                      <p className="text-xs text-gray-500 mb-3">{t.teams.claimHelp}</p>
                      {/* #569 — a record with a pending claim stays in the list: the real
                          athlete can send a rival claim, and the reviewer compares them. */}
                      {athleteRecords.filter(r => r.claim_status !== 'claimed').length > 0 ? (
                        <div className="space-y-2">
                          {athleteRecords.filter(r => r.claim_status !== 'claimed').map(rec => (
                            <div key={rec.id} data-athlete-record-id={rec.id} className="card p-3 flex items-center gap-3">
                              <AthleteAvatar firstName={rec.first_name} lastName={rec.last_name} size="sm" country={rec.country} />
                              <div className="flex-1">
                                <div className="font-medium text-sm">{personName(rec.first_name, rec.last_name)}</div>
                                <div className="text-xs text-gray-500">
                                  {rec.gender ? genderLabel(rec.gender) : '—'} · {weightText(rec.current_weight_kg) ?? '—'} · {boutCount(rec.experience_bouts ?? 0)}
                                </div>
                              </div>
                              {rec.claim_status === 'pending' && <span className={`badge ${BADGE_XS} bg-orange-50 text-orange-700 shrink-0`}>{t.teams.claimsPending}</span>}
                              <button onClick={() => setClaimTarget(rec)} className="btn-primary btn-sm shrink-0">{t.teams.thisIsMe}</button>
                            </div>
                          ))}
                        </div>
                      ) : !listsError && (
                        <p className="text-sm text-gray-400">{t.teams.noUnclaimedRecords}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      )}
    </div>
  );
}

